// ─────────────────────────────────────────────────────────────────────────────
// Decoding the picture and drawing the result. The screen preview and the
// exported file are drawn by the same function, so what the preview shows is
// what is saved.
// ─────────────────────────────────────────────────────────────────────────────

import { cropRects, type CropBounds, type CropState, type Size } from './geometry.js';

/** A decoded picture, already turned the right way up. */
export interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** Free the decoded pixels. */
  close: () => void;
}

/**
 * Decode a picture, honouring its EXIF orientation, so a phone photo taken on
 * its side is not cropped on its side.
 *
 * `createImageBitmap` with `imageOrientation: 'from-image'` is the reliable
 * way. Where it is missing, or refuses the option, an `<img>` is used: every
 * current browser applies EXIF orientation to images by default.
 */
export async function decodeImage(file: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    for (const options of [{ imageOrientation: 'from-image' } as ImageBitmapOptions, undefined]) {
      try {
        const bitmap = options ? await createImageBitmap(file, options) : await createImageBitmap(file);
        if (bitmap.width > 0 && bitmap.height > 0) {
          return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
        }
        bitmap.close();
      } catch {
        // Try the next way.
      }
    }
  }
  return decodeWithImageElement(file);
}

function decodeWithImageElement(file: Blob): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        URL.revokeObjectURL(url);
        reject(new Error('The picture has no size.'));
        return;
      }
      resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The picture could not be decoded.'));
    };
    img.src = url;
  });
}

/**
 * Draw the result into a context whose coordinate space is the output
 * (`frame`): the background first, so padding and see-through pixels are
 * flattened onto it, then the part of the picture inside the frame. Scale the
 * context beforehand to draw a smaller copy (the preview).
 */
export function drawCrop(
  ctx: CanvasRenderingContext2D,
  image: DecodedImage,
  frame: Size,
  state: CropState,
  bounds: CropBounds,
  background: string
): void {
  ctx.save();
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, frame.width, frame.height);
  const { source, dest } = cropRects(image, frame, state, bounds);
  if (source.width > 0 && source.height > 0 && dest.width > 0 && dest.height > 0) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image.source, source.x, source.y, source.width, source.height, dest.x, dest.y, dest.width, dest.height);
  }
  ctx.restore();
}

export interface RenderOptions {
  width: number;
  height: number;
  state: CropState;
  bounds: CropBounds;
  background: string;
  type: string;
  quality: number;
}

/** The finished picture, exactly `width` × `height`, as a Blob of `type`. */
export async function renderCrop(image: DecodedImage, opts: RenderOptions): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(opts.width);
  canvas.height = Math.round(opts.height);
  const ctx = canvas.getContext('2d', { alpha: opts.type !== 'image/jpeg' });
  if (!ctx) throw new Error('No 2D canvas.');
  drawCrop(ctx, image, { width: canvas.width, height: canvas.height }, opts.state, opts.bounds, opts.background);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, opts.type, opts.quality));
  if (!blob) throw new Error('The canvas gave no picture.');
  // A browser that cannot write the asked-for type quietly writes PNG. Say so rather than mislabel the file.
  if (blob.type && blob.type !== opts.type) throw new Error(`This browser cannot write ${opts.type}.`);
  return blob;
}

/** "JPG" for image/jpeg, and so on: the word a person knows the format by. */
export function formatName(type: string): string {
  if (type === 'image/jpeg') return 'JPG';
  if (type === 'image/png') return 'PNG';
  if (type === 'image/webp') return 'WebP';
  return type.replace(/^image\//, '').toUpperCase();
}

/** "profile-picture.jpg" from a name and a type, keeping the name's stem. */
export function outputFileName(name: string | undefined, type: string): string {
  const ext = type === 'image/jpeg' ? 'jpg' : type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'img';
  const stem = (name ?? '').replace(/\.[^./\\]+$/, '').trim() || 'picture';
  return `${stem}.${ext}`;
}
